import { Round as ArrowDown } from '@manomano-internal/tamagoshi-icons/esm/material/ExpandMore';
import dynamic from 'next/dynamic';
import { useEffect, type FC } from 'react';
import type React from 'react';
import { useDispatch } from 'react-redux';

import { Info } from '@/core/tamagoshi/icons/Info';
import { Option } from '@/core/tamagoshi/SelectInput/Option';
import { SelectInput } from '@/core/tamagoshi/SelectInput/SelectInput';
import { ArticleReviewsProvider } from '@/domains/productDiscovery/ArticleReviews/ArticleReviewsProvider';
import { ReviewCard } from '@/domains/productDiscovery/ArticleReviews/components/ReviewCard/ReviewCard';
import {
  useArticleReviews,
  type Order,
} from '@/domains/productDiscovery/ArticleReviews/hooks/useArticleReviews/useArticleReviews';
import { ReviewsStatsModal } from '@/domains/productDiscovery/ArticleReviews/modules/allReviews/reviewsStatsModal/ReviewsStatsModal';
import { GET_REVIEWS_STATS_TRIGGER } from '@/domains/productDiscovery/ArticleReviews/root.ducks.default';
import {
  latestReviewsSelectOption,
  relevantReviewsSelectOption,
  reviewConsumerInformationRoute,
  reviewsAllCommentsTitle,
  reviewsMonitoredReviews,
  reviewsNoReviews,
  reviewsViewMore,
  sortInputTitle,
  usefulReviewsSelectOption,
} from '@/domains/productDiscovery/ArticleReviews/translations';
import { Block } from '@/domains/productDiscovery/ProductPage/b2c/components/Block/Block';
import { useDetail } from '@/domains/productDiscovery/ProductPage/hooks/useDetail';

import styles from './allreviews.module.scss';

export type ReviewsKind = 'all' | 'b2b';

interface AllReviewsProps {
  kind?: ReviewsKind;
}

const B2BFilter = dynamic(
  import('./components/B2BHeader/B2BHeader').then((module) => module.B2BFilter),
);

export const AllReviews: FC<AllReviewsProps> = () => {
  const { articleId } = useDetail();

  const {
    reviews,
    hasMoreReviews,
    starsFilter,
    toggleB2bOnly,
    onlyB2b,
    toggleStarsFilter,
    fetchMoreReviews,
    clearStarsFilter,
    orderBy,
    orderReview,
  } = useArticleReviews(articleId, 10);

  const dispatch = useDispatch();

  useEffect(() => {
    dispatch({ type: GET_REVIEWS_STATS_TRIGGER, articleId });
  }, [dispatch, articleId]);

  const onKeyPress = (event: React.KeyboardEvent) =>
    event.key === 'Enter' && fetchMoreReviews();

  return (
    <ArticleReviewsProvider>
      <Block className={styles.blockContainer} isTab>
        <B2BFilter state={onlyB2b} onChange={toggleB2bOnly} />
        <div className={styles.statsContainer}>
          <ReviewsStatsModal
            clearStarsFilter={clearStarsFilter}
            toggleStarsFilter={toggleStarsFilter}
            starsFilter={starsFilter}
          />
        </div>
        <div className={styles.disclaimer}>
          <span>
            <a
              href={reviewConsumerInformationRoute}
              target="_blank"
              rel="noopener noreferrer nofollow"
              className={styles.link}
            >
              <Info className={styles.info} />
            </a>
            {reviewsMonitoredReviews}
          </span>
        </div>
        <div className={styles.allReviewsContainer}>
          <div className={styles.allReviewsTitleContainer}>
            <div className={styles.allReviewsTitle}>
              {reviewsAllCommentsTitle}
            </div>
            <SelectInput
              className={styles.orderBy}
              status="normal"
              value={orderBy}
              label={sortInputTitle}
              onChange={(event, value) => orderReview(value as Order)}
            >
              <Option value="reviewScore">{relevantReviewsSelectOption}</Option>
              <Option value="upVoteCount">{usefulReviewsSelectOption}</Option>
              <Option value="createdAt">{latestReviewsSelectOption}</Option>
            </SelectInput>
          </div>

          {reviews.length > 0 ? (
            reviews.map((review) => (
              <ReviewCard {...review} key={review.reviewId} />
            ))
          ) : (
            <span className={styles.noReviews}>{reviewsNoReviews}</span>
          )}
          {hasMoreReviews && (
            <div
              role="button"
              tabIndex={0}
              className={styles.showMore}
              onClick={fetchMoreReviews}
              onKeyDown={onKeyPress}
              data-testid="see-more-reviews"
            >
              <ArrowDown />
              <div className={styles.moreText}>{reviewsViewMore}</div>
            </div>
          )}
        </div>
      </Block>
    </ArticleReviewsProvider>
  );
};
